import React, { useCallback, useEffect, useRef, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useAuth } from "../auth/AuthContext";
import { supportApi } from "../services/api";
import { mascotSrc, subscribeMascot } from "../utils/mascotBus";

const SHOW_MS = 7000;
const INBOX_MS = 45000;

// Inbox entries the mascot reacts to, richest match first.
const INBOX_MOODS = [
  { test: (item) => item.kind === "withdraw" && /onay/i.test(item.title || ""), mood: "win", tone: "win" },
  { test: (item) => item.kind === "withdraw", mood: "sad", tone: "sad" },
  { test: (item) => item.kind === "deposit" || item.kind === "payment", mood: "ok", tone: "win" },
  { test: (item) => item.kind === "bonus", mood: "tease", tone: "gold" },
  { test: (item) => item.kind === "transfer", mood: "thanks", tone: "calm" },
];

const MascotPopup = () => {
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const timer = useRef(null);
  const seenInbox = useRef(null);

  const close = useCallback(() => {
    clearTimeout(timer.current);
    setItem(null);
  }, []);

  useEffect(
    () =>
      subscribeMascot((detail) => {
        clearTimeout(timer.current);
        setItem({ ...detail, id: Date.now() });
        timer.current = setTimeout(() => setItem(null), detail.duration || SHOW_MS);
      }),
    []
  );

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!user) {
      seenInbox.current = null;
      return undefined;
    }

    let active = true;
    const check = () => {
      supportApi
        .inbox()
        .then((data) => {
          if (!active) return;
          const items = data.items || [];
          const newest = items.reduce((max, entry) => Math.max(max, Number(entry.id) || 0), 0);
          // First pass only records where we are, old mail should not pop up.
          if (seenInbox.current === null) {
            seenInbox.current = newest;
            return;
          }
          const fresh = items
            .filter((entry) => Number(entry.id) > seenInbox.current)
            .sort((a, b) => Number(a.id) - Number(b.id));
          seenInbox.current = Math.max(seenInbox.current, newest);
          const hit = fresh.find((entry) => INBOX_MOODS.some((rule) => rule.test(entry)));
          if (!hit) return;
          const rule = INBOX_MOODS.find((entry) => entry.test(hit));
          clearTimeout(timer.current);
          setItem({ id: Date.now(), mood: rule.mood, tone: rule.tone, title: hit.title, text: hit.text });
          timer.current = setTimeout(() => setItem(null), SHOW_MS + 2000);
        })
        .catch(() => {});
    };

    check();
    const id = setInterval(check, INBOX_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [user]);

  if (!item) return null;

  return (
    <aside className={`mascot-pop tone-${item.tone || "calm"}`} key={item.id} role="status">
      <img className="mascot-pop-art" src={mascotSrc(item.mood)} alt="" />
      <div className="mascot-pop-body">
        <b>{item.title}</b>
        {item.text ? <span>{item.text}</span> : null}
      </div>
      <button type="button" className="mascot-pop-close" onClick={close} aria-label="Kapat">
        <CloseRoundedIcon fontSize="small" />
      </button>
    </aside>
  );
};

export default MascotPopup;
